import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../../lib/firebase";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { Video, CheckCircle, ArrowLeft, BookOpen, FileText, Upload, X, Lightbulb, Plus, HelpCircle, Edit } from "lucide-react";
import MediaUpload from "../../components/MediaUpload";
import { useMediaUpload } from "../../hooks/useMediaUpload";
import { getUploadUrlWithQuota } from "../../services/uploadWithQuota";

export default function AddLesson() {
  const { id } = useParams();
  const nav = useNavigate();
  const { uploadMedia, uploading: uploadingMedia, progress } = useMediaUpload();

  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [contentType, setContentType] = useState<'video' | 'image'>('video');
  const [videoUrl, setVideoUrl] = useState("");
  const [videoJobId, setVideoJobId] = useState<string | null>(null);
  const [videoUploaded, setVideoUploaded] = useState(false);
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [coverImageUploaded, setCoverImageUploaded] = useState(false);
  const [attachments, setAttachments] = useState<{name: string, url: string, size: number, displayName?: string}[]>([]);
  const [editingAttachmentIndex, setEditingAttachmentIndex] = useState<number | null>(null);
  const [tempDisplayName, setTempDisplayName] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState<{question: string, choices: string[], correctAnswer: number}[]>([]);

  const [order, setOrder] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load number of existing lessons
  useEffect(() => {
    async function load() {
      if (!id) return;
      const q = query(collection(db, "lessons"), where("courseId", "==", id));
      const snap = await getDocs(q);
      setOrder(snap.size + 1);
    }
    load();
  }, [id]);

  const handleVideoUpload = (mediaId: string, url: string, jobId?: string) => {
    setVideoUrl(url);
    setVideoJobId(jobId || null);
    setVideoUploaded(true);
    console.log('📹 Video uploaded with jobId:', jobId);
  };

  const handleImageUpload = (mediaId: string, url: string) => {
    setCoverImageUrl(url);
    setCoverImageUploaded(true);
    console.log('📸 Cover image uploaded:', url);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;

    // Check file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      alert("ไฟล์มีขนาดใหญ่เกิน 50MB");
      return;
    }

    setUploadingFile(true);
    try {
      // Upload file to R2 using Cloudflare Worker
      console.log('[AddLesson] Uploading file:', file.name, file.size);
      const result = await getUploadUrlWithQuota(
        file.name,
        file.type,
        file.size,
        file
      );
      
      console.log('[AddLesson] Upload successful:', result);
      
      setAttachments([...attachments, {
        name: file.name,
        url: result.publicUrl,
        size: file.size,
        displayName: "ไฟล์ประกอบการเรียน" // Default name
      }]);

      // Show success message
      const successMsg = document.createElement('div');
      successMsg.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-xl z-50';
      successMsg.textContent = '✓ อัปโหลดไฟล์สำเร็จ!';
      document.body.appendChild(successMsg);
      setTimeout(() => successMsg.remove(), 2000);
    } catch (error: any) {
      console.error("Error uploading file:", error);
      alert(error.message || "เกิดข้อผิดพลาดในการอัปโหลดไฟล์");
    }
    setUploadingFile(false);
    // Reset input
    e.target.value = '';
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const updateAttachmentDisplayName = (index: number, displayName: string) => {
    const updated = [...attachments];
    updated[index].displayName = displayName.trim() || "ไฟล์ประกอบการเรียน";
    setAttachments(updated);
    setEditingAttachmentIndex(null);
    setTempDisplayName("");
  };

  const addQuizQuestion = () => {
    setQuizQuestions([...quizQuestions, {
      question: "",
      choices: ["", ""],
      correctAnswer: 0
    }]);
  };

  const updateQuizQuestion = (index: number, field: string, value: any) => {
    const updated = [...quizQuestions];
    if (field === 'question') {
      updated[index].question = value;
    } else if (field === 'correctAnswer') {
      updated[index].correctAnswer = value;
    }
    setQuizQuestions(updated);
  };

  const updateQuizChoice = (qIndex: number, cIndex: number, value: string) => {
    const updated = [...quizQuestions];
    updated[qIndex].choices[cIndex] = value;
    setQuizQuestions(updated);
  };

  const addQuizChoice = (qIndex: number) => {
    const updated = [...quizQuestions];
    updated[qIndex].choices.push("");
    setQuizQuestions(updated);
  };

  const removeQuizChoice = (qIndex: number, cIndex: number) => {
    const updated = [...quizQuestions];
    if (updated[qIndex].choices.length > 2) {
      updated[qIndex].choices.splice(cIndex, 1);
      if (updated[qIndex].correctAnswer >= cIndex && updated[qIndex].correctAnswer > 0) {
        updated[qIndex].correctAnswer--;
      }
      setQuizQuestions(updated);
    }
  };

  const removeQuizQuestion = (index: number) => {
    setQuizQuestions(quizQuestions.filter((_, i) => i !== index));
  };

  const save = async () => {
    // Check if we have content (video or image) and title
    const hasContent = contentType === 'video' ? (videoJobId || videoUrl) : coverImageUrl;
    if (!id || !hasContent || !title) {
      alert("กรุณากรอกข้อมูลให้ครบถ้วน แลวอัปโหลดวิดีโอหรือรูปภาพปก");
      return;
    }

    setSaving(true);

    try {
      await addDoc(collection(db, "lessons"), {
        courseId: id,
        title,
        description: desc,
        contentType: contentType,
        videoUrl: contentType === 'video' ? (videoUrl || "") : "",
        videoJobId: contentType === 'video' ? videoJobId : null,
        coverImageUrl: contentType === 'image' ? coverImageUrl : "",
        quizQuestions: quizQuestions,
        orderIndex: order,
        attachments: attachments,
      });

      // Show success message
      const successMsg = document.createElement('div');
      successMsg.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-xl z-50 animate-fadeIn';
      successMsg.textContent = '✓ เพิ่มบทเรียนสำเร็จ!';
      document.body.appendChild(successMsg);
      setTimeout(() => {
        successMsg.remove();
        nav(`/instructor/courses/${id}/lessons`);
      }, 1500);
    } catch (error) {
      console.error("Error adding lesson:", error);
      alert("เกิดข้อผิดพลาดในการเพิ่มบทเรียน");
      setSaving(false);
    }
  };

  // Check if we have content based on content type
  const hasContent = contentType === 'video' ? (videoJobId || videoUrl) : coverImageUrl;
  const isValid = hasContent && title;
  
  // Debug logging
  console.log('🔍 AddLesson validation:', {
    contentType,
    videoUrl,
    videoJobId,
    coverImageUrl,
    hasContent,
    title,
    isValid
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-pink-50/20 py-4 md:py-8 px-3 md:px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-4 md:mb-8">
          <button
            onClick={() => nav(`/instructor/courses/${id}/lessons`)}
            className="flex items-center gap-1.5 md:gap-2 text-textLight hover:text-textDark text-sm md:text-base transition-colors mb-3 md:mb-4"
          >
            <ArrowLeft className="w-4 md:w-5 h-4 md:h-5" />
            กลับไปรายการบทเรียน
          </button>
          
          <div className="flex items-center gap-3 md:gap-4">
            <div className="bg-blue-100 p-3 md:p-4 rounded-xl md:rounded-2xl">
              <BookOpen className="w-8 md:w-10 h-8 md:h-10 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-textDark mb-1 md:mb-2">เพิ่มบทเรียนใหม่</h1>
              <p className="text-textLight text-sm md:text-lg">บทเรียนที่ {order}</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-4 md:gap-8">
          {/* Left Column - Form */}
          <div className="space-y-4 md:space-y-6">
            {/* Content Type Selection */}
            <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 md:p-8">
              <h3 className="text-base md:text-lg font-bold text-textDark mb-3 md:mb-4">เลือกประเภทเนื้อหาบทเรียน</h3>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <button
                  type="button"
                  onClick={() => setContentType('video')}
                  className={`p-4 md:p-6 rounded-xl border-2 transition-all ${
                    contentType === 'video'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  }`}
                >
                  <Video className={`w-8 md:w-10 h-8 md:h-10 mx-auto mb-2 md:mb-3 ${
                    contentType === 'video' ? 'text-blue-600' : 'text-gray-400'
                  }`} />
                  <p className={`text-sm md:text-base font-semibold ${
                    contentType === 'video' ? 'text-blue-600' : 'text-gray-600'
                  }`}>วิดีโอ</p>
                  <p className="text-xs text-gray-500 mt-1">สำหรับสอนแบบวิดีโอ</p>
                </button>
                <button
                  type="button"
                  onClick={() => setContentType('image')}
                  className={`p-4 md:p-6 rounded-xl border-2 transition-all ${
                    contentType === 'image'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  }`}
                >
                  <FileText className={`w-8 md:w-10 h-8 md:h-10 mx-auto mb-2 md:mb-3 ${
                    contentType === 'image' ? 'text-blue-600' : 'text-gray-400'
                  }`} />
                  <p className={`text-sm md:text-base font-semibold ${
                    contentType === 'image' ? 'text-blue-600' : 'text-gray-600'
                  }`}>รูปภาพปก</p>
                  <p className="text-xs text-gray-500 mt-1">สำหรับ E-book/เอกสาร</p>
                </button>
              </div>
            </div>

            {/* Video Upload */}
            {contentType === 'video' && (
            <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 md:p-8">
              <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
                <div className="bg-blue-100 p-2 md:p-3 rounded-lg md:rounded-xl">
                  <Video className="w-5 md:w-6 h-5 md:h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg md:text-2xl font-bold text-textDark">อัปโหลดวิดีโอ</h2>
                  <p className="text-xs md:text-sm text-textLight">อัปโหลดไฟล์วิดีโอของคุณ</p>
                </div>
              </div>

              {uploadingMedia && progress ? (
                // Uploading state with progress bar
                <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-lg border-2 border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-blue-600">
                      <Video className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{progress.fileName}</p>
                      <p className="text-sm text-gray-600">
                        {progress.status === "uploading" && `กำลังอัปโหลด... ${progress.progress}%`}
                        {progress.status === "success" && "อัปโหลดสำเร็จ!"}
                        {progress.status === "error" && `เกิดข้อผิดพลาด: ${progress.error}`}
                      </p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                    <div
                      className="bg-gradient-to-r from-blue-600 to-pink-600 h-4 rounded-full transition-all duration-300 flex items-center justify-center"
                      style={{ width: `${progress.progress}%` }}
                    >
                      <span className="text-xs font-bold text-white">{progress.progress}%</span>
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-500 text-center">
                    กรุณารอจนกว่าการอัปโหลดจะเสร็จสมบูรณ์
                  </p>
                </div>
              ) : !videoUploaded ? (
                <MediaUpload
                  courseId={id!}
                  mediaType="video"
                  onUploadComplete={handleVideoUpload}
                  onUploadError={(error) => alert(error)}
                />
              ) : (
                <div className="space-y-4">
                  {/* Video Preview */}
                  <div className="bg-black rounded-2xl overflow-hidden">
                    <div className="aspect-video">
                      <video
                        src={videoUrl}
                        controls
                        controlsList="nodownload"
                        className="w-full h-full"
                        preload="metadata"
                      >
                        <track kind="captions" />
                      </video>
                    </div>
                  </div>
                  
                  {/* Video Processing Status */}
                  <div className="p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-semibold">อัปโหลดวิดีโอสำเร็จ!</span>
                    </div>
                    <p className="text-xs text-green-700 mb-3">
                      คุณสามารถกด "เพิ่มบทเรียน" ได้ทันที - ระบบจะประมวลผลวิดีโอให้เหมาะกับทุกอุปกรณ์โดยอัตโนมัติในเบื้องหลัง
                    </p>
                    <button
                      onClick={() => {
                        setVideoUrl("");
                        setVideoUploaded(false);
                        setVideoJobId(null);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
                    >
                      อัปโหลดวิดีโอใหม่
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-3 md:mt-4 p-3 md:p-4 bg-blue-50 border-2 border-blue-200 rounded-lg md:rounded-xl">
                <p className="text-xs md:text-sm text-blue-800 font-semibold mb-1.5 md:mb-2 flex items-center gap-1.5 md:gap-2">
                  <Lightbulb className="w-3.5 md:w-4 h-3.5 md:h-4" />
                  เคล็ดลับ:
                </p>
                <ul className="text-[10px] md:text-xs text-blue-700 space-y-0.5 md:space-y-1">
                  <li>• รองรับไฟล์ .mp4, .mov, .avi, .mkv</li>
                  <li>• ขนาดสูงสุด: 1 GB (1024 MB)</li>
                  <li>• ขนาดแนะนำ: 300-500 MB เพื่อประหยัดพื้นที่และอัปโหลดเร็วขึ้น</li>
                  <li>• วิดีโอยาวเกิน 1 ชั่วโมง แนะนำให้ตัดเป็นคลิปสั้น ๆ หลายคลิป</li>
                  <li>• คลิปสั้นช่วยให้ผู้เรียนเข้าใจง่าย ทบทวนสะดวก และอัปโหลดเร็วกว่า</li>
                  <li>• อัปโหลด 100% แล้วกดเพิ่มบทเรียนได้เลย ไม่ต้องรอ</li>
                  <li>• ระบบจะประมวลผลวิดีโอในเบื้องหลังโดยอัตโนมัติ</li>
                </ul>
              </div>
            </div>
            )}

            {/* Image Cover Upload */}
            {contentType === 'image' && (
            <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 md:p-8">
              <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
                <div className="bg-blue-100 p-2 md:p-3 rounded-lg md:rounded-xl">
                  <FileText className="w-5 md:w-6 h-5 md:h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg md:text-2xl font-bold text-textDark">อัปโหลดรูปภาพปก</h2>
                  <p className="text-xs md:text-sm text-textLight">อัปโหลดรูปภาพปกสำหรับ E-book หรือเอกสาร</p>
                </div>
              </div>

              {!coverImageUploaded ? (
                <MediaUpload
                  courseId={id!}
                  mediaType="image"
                  onUploadComplete={handleImageUpload}
                  onUploadError={(error) => alert(error)}
                />
              ) : (
                <div className="space-y-4">
                  {/* Image Preview */}
                  <div className="bg-gray-100 rounded-2xl overflow-hidden">
                    <img
                      src={coverImageUrl}
                      alt="Cover"
                      className="w-full h-auto"
                    />
                  </div>
                  
                  {/* Upload Success */}
                  <div className="p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-semibold">อัปโหลดรูปภาพสำเร็จ!</span>
                    </div>
                    <button
                      onClick={() => {
                        setCoverImageUrl("");
                        setCoverImageUploaded(false);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
                    >
                      อัปโหลดรูปภาพใหม่
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-3 md:mt-4 p-3 md:p-4 bg-blue-50 border-2 border-blue-200 rounded-lg md:rounded-xl">
                <p className="text-xs md:text-sm text-blue-800 font-semibold mb-1.5 md:mb-2 flex items-center gap-1.5 md:gap-2">
                  <Lightbulb className="w-3.5 md:w-4 h-3.5 md:h-4" />
                  เคล็ดลับ:
                </p>
                <ul className="text-[10px] md:text-xs text-blue-700 space-y-0.5 md:space-y-1">
                  <li>• รองรับไฟล์ .jpg, .png, .webp</li>
                  <li>• ขนาดสูงสุด: 10 MB</li>
                  <li>• ขนาดแนะนำ: 1920x1080 พิกเซล (16:9)</li>
                  <li>• แนะนำใช้รูปภาพที่มีรายละเอียดของ E-book หรือเอกสาร</li>
                  <li>• ผู้เรียนจะเห็นรูปภาพนี้แทนวิดีโอ</li>
                  <li>• สามารถแนบไฟล์ PDF ได้ที่ด้านล่าง</li>
                </ul>
              </div>
            </div>
            )}

            {/* File Attachments Upload */}
            <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 md:p-8">
              <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
                <div className="bg-blue-100 p-2 md:p-3 rounded-lg md:rounded-xl">
                  <FileText className="w-5 md:w-6 h-5 md:h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg md:text-2xl font-bold text-textDark">ไฟล์ประกอบการสอน</h2>
                  <p className="text-xs md:text-sm text-textLight">อัปโหลดไฟล์เอกสาร PDF, Word, Excel (ถ้ามี)</p>
                </div>
              </div>

              {/* Upload Button */}
              <label className="block">
                <input
                  type="file"
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
                  className="hidden"
                  disabled={uploadingFile}
                />
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all">
                  {uploadingFile ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <p className="text-sm text-gray-600">กำลังอัปโหลด...</p>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm font-semibold text-gray-700">คลิกเพื่ออัปโหลดไฟล์</p>
                      <p className="text-xs text-gray-500 mt-1">PDF, Word, Excel, PowerPoint (สูงสุด 50MB)</p>
                    </div>
                  )}
                </div>
              </label>

              {/* Attachments List */}
              {attachments.length > 0 && (
                <div className="mt-4 space-y-2">
                  {attachments.map((file, index) => (
                    <div key={index} className="bg-blue-50 rounded-xl overflow-hidden">
                      <div className="flex items-center justify-between p-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            {editingAttachmentIndex === index ? (
                              <input
                                type="text"
                                value={tempDisplayName}
                                onChange={(e) => setTempDisplayName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    updateAttachmentDisplayName(index, tempDisplayName);
                                  } else if (e.key === 'Escape') {
                                    setEditingAttachmentIndex(null);
                                    setTempDisplayName("");
                                  }
                                }}
                                className="w-full px-2 py-1 text-sm font-semibold text-gray-800 border-2 border-blue-400 rounded focus:outline-none"
                                placeholder="ชื่อไฟล์ที่แสดง"
                                autoFocus
                              />
                            ) : (
                              <p className="text-sm font-semibold text-gray-800 truncate">
                                {file.displayName || "ไฟล์ประกอบการเรียน"}
                              </p>
                            )}
                            <p className="text-xs text-gray-500">{file.name} • {(file.size / 1024 / 1024).toFixed(2)} MB</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {editingAttachmentIndex === index ? (
                            <>
                              <button
                                onClick={() => updateAttachmentDisplayName(index, tempDisplayName)}
                                className="p-2 hover:bg-green-100 rounded-lg transition-colors"
                                title="บันทึก"
                              >
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingAttachmentIndex(null);
                                  setTempDisplayName("");
                                }}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                title="ยกเลิก"
                              >
                                <X className="w-4 h-4 text-gray-600" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  setEditingAttachmentIndex(index);
                                  setTempDisplayName(file.displayName || "ไฟล์ประกอบการเรียน");
                                }}
                                className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                                title="แก้ไขชื่อ"
                              >
                                <Edit className="w-4 h-4 text-blue-600" />
                              </button>
                              <button
                                onClick={() => removeAttachment(index)}
                                className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                                title="ลบไฟล์"
                              >
                                <X className="w-4 h-4 text-red-600" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Lesson Details */}
            <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 md:p-8">
              <h2 className="text-lg md:text-2xl font-bold text-textDark mb-4 md:mb-6">รายละเอียดบทเรียน</h2>

              <div className="space-y-3 md:space-y-4">
                <div>
                  <label className="block text-xs md:text-sm font-bold text-textDark mb-1.5 md:mb-2">
                    ชื่อบทเรียน *
                  </label>
                  <input
                    type="text"
                    placeholder="เช่น: บทที่ 1 - แนะนำพื้นฐาน"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none transition-colors text-sm md:text-lg"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-bold text-textDark mb-1.5 md:mb-2">
                    คำอธิบาย
                  </label>
                  <textarea
                    placeholder="อธิบายสิ่งที่นักเรียนจะได้เรียนรู้ในบทนี้..."
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    rows={4}
                    className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none transition-colors resize-none text-sm md:text-base"
                  />
                  <p className="text-xs md:text-sm text-textLight mt-1.5 md:mt-2">
                    {desc.length} ตัวอักษร
                  </p>
                </div>
              </div>
            </div>

            {/* Quiz Questions Section */}
            <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 md:p-8">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="bg-blue-100 p-2 md:p-3 rounded-lg md:rounded-xl">
                    <HelpCircle className="w-5 md:w-6 h-5 md:h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg md:text-2xl font-bold text-textDark">คำถามทบทวน</h2>
                    <p className="text-xs md:text-sm text-textLight hidden md:block">เพิ่มคำถามเพื่อทดสอบความเข้าใจ (ไม่บังคับ)</p>
                  </div>
                </div>
                <button
                  onClick={addQuizQuestion}
                  className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-gradient-to-r from-blue-500 to-pink-500 text-white rounded-lg md:rounded-xl text-xs md:text-base font-semibold hover:shadow-lg transition-all"
                >
                  <Plus className="w-4 md:w-5 h-4 md:h-5" />
                  <span className="hidden sm:inline">เพิ่มคำถาม</span>
                  <span className="sm:hidden">เพิ่ม</span>
                </button>
              </div>

              {quizQuestions.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
                  <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">ยังไม่มีคำถามทบทวน</p>
                  <p className="text-sm text-gray-400 mt-1">คลิก "เพิ่มคำถาม" เพื่อสร้างคำถามสำหรับนักเรียน</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {quizQuestions.map((quiz, qIndex) => (
                    <div key={qIndex} className="border-2 border-blue-100 rounded-2xl p-6 bg-blue-50/30">
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-800">คำถามที่ {qIndex + 1}</h3>
                        <button
                          onClick={() => removeQuizQuestion(qIndex)}
                          className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                          title="ลบคำถาม"
                        >
                          <X className="w-5 h-5 text-red-600" />
                        </button>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            คำถาม
                          </label>
                          <input
                            type="text"
                            placeholder="พิมพ์คำถามของคุณ..."
                            value={quiz.question}
                            onChange={(e) => updateQuizQuestion(qIndex, 'question', e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-400 focus:outline-none transition-colors"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            ตัวเลือก
                          </label>
                          <div className="space-y-2">
                            {quiz.choices.map((choice, cIndex) => (
                              <div key={cIndex} className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name={`correct-${qIndex}`}
                                  checked={quiz.correctAnswer === cIndex}
                                  onChange={() => updateQuizQuestion(qIndex, 'correctAnswer', cIndex)}
                                  className="w-5 h-5 text-blue-600 focus:ring-blue-500"
                                />
                                <input
                                  type="text"
                                  placeholder={`ตัวเลือกที่ ${cIndex + 1}`}
                                  value={choice}
                                  onChange={(e) => updateQuizChoice(qIndex, cIndex, e.target.value)}
                                  className="flex-1 px-4 py-2 rounded-lg border-2 border-gray-200 focus:border-blue-400 focus:outline-none transition-colors"
                                />
                                {quiz.choices.length > 2 && (
                                  <button
                                    onClick={() => removeQuizChoice(qIndex, cIndex)}
                                    className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                                    title="ลบตัวเลือก"
                                  >
                                    <X className="w-4 h-4 text-red-600" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                          <button
                            onClick={() => addQuizChoice(qIndex)}
                            className="mt-2 flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-100 rounded-lg font-semibold transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                            เพิ่มตัวเลือก
                          </button>
                        </div>

                        <div className="bg-blue-100 border-2 border-blue-200 rounded-lg p-3">
                          <p className="text-xs text-blue-800 font-semibold">
                            คำตอบที่ถูกต้อง: {quiz.choices[quiz.correctAnswer] || 'ยังไม่ได้เลือก'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Preview */}
          <div className="lg:sticky lg:top-8 h-fit">
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 md:mt-8 flex items-center justify-between bg-white rounded-xl md:rounded-2xl shadow-xl p-4 md:p-6">
          <button
            onClick={() => nav(`/instructor/courses/${id}/lessons`)}
            className="px-4 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl text-sm md:text-base font-semibold text-textDark bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            ยกเลิก
          </button>
          
          <button
            onClick={save}
            disabled={!isValid || saving}
            className="px-6 md:px-8 py-2 md:py-3 rounded-lg md:rounded-xl text-sm md:text-base font-semibold text-white bg-gradient-primary hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-1.5 md:gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                กำลังบันทึก...
              </>
            ) : (
              <>  
                <CheckCircle className="w-5 h-5" />
                เพิ่มบทเรียน
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
